import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface ScanLoadingModalProps {
  open: boolean;
  onCancel?: () => void;
  progress?: number; // 0-100
  stage?: number; // 1-4
  contentType?: 'case_study' | 'shot'; // Type of content being imported
}

const caseStudyStages = [
  { id: 1, message: "Accessing page..." },
  { id: 2, message: "Extracting content..." },
  { id: 3, message: "Analyzing structure..." },
  { id: 4, message: "Preparing your case study..." },
];

const shotStages = [
  { id: 1, message: "Accessing page..." },
  { id: 2, message: "Extracting content..." },
  { id: 3, message: "Analyzing structure..." },
  { id: 4, message: "Preparing your shot..." },
];

export const ScanLoadingModal: React.FC<ScanLoadingModalProps> = ({
  open,
  onCancel,
  progress = 0,
  stage = 1,
  contentType = 'case_study',
}) => {
  const [smoothProgress, setSmoothProgress] = useState(1);
  const [targetProgress, setTargetProgress] = useState(progress);
  const loadingStages = contentType === 'shot' ? shotStages : caseStudyStages;
  const currentStage = loadingStages[stage - 1] || loadingStages[0];

  // Update target when prop changes
  useEffect(() => {
    setTargetProgress(progress);
  }, [progress]);

  // Smoothly increment progress
  useEffect(() => {
    if (!open) {
      setSmoothProgress(1);
      return;
    }

    const interval = setInterval(() => {
      setSmoothProgress((current) => {
        // If we're below target, increment
        if (current < targetProgress) {
          const diff = targetProgress - current;
          // Slower increment when above 60%
          const increment = current > 70 ? Math.max(0.2, diff / 25) : current > 50 ? Math.max(0.4, diff / 20) : Math.max(0.6, diff / 15);
          return Math.min(targetProgress, current + increment);
        }

        // Really slow creep from 95-99%
        if (current >= 95 && current < 100) {
          return current + 0.05; // Very slow creep for final stretch
        }

        return current;
      });
    }, 150); // Update every 150ms for smooth animation

    return () => clearInterval(interval);
  }, [open, targetProgress, stage]);

  const displayProgress = Math.min(100, Math.max(1, smoothProgress));

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-[800px] bg-white rounded-lg px-20 py-10">
        <div className="flex flex-col items-center justify-center space-y-6">
          <h2 className="text-2xl font-bold text-center">Scanning Website</h2>
          <p className="text-gray-600 text-center text-sm">
            {contentType === 'shot'
              ? 'Processing website and gathering shot details'
              : 'Processing website and gathering case study details'
            }
          </p>

          {/* Circular progress indicator */}
          <div className="py-8 relative">
            <svg
              width="120"
              height="120"
              viewBox="0 0 120 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Background circle */}
              <circle
                cx="60"
                cy="60"
                r="52"
                stroke="#E5E7EB"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress circle */}
              <circle
                cx="60"
                cy="60"
                r="52"
                stroke="#10B981"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - displayProgress / 100)}`}
                transform="rotate(-90 60 60)"
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            {/* Percentage text centered in circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold text-gray-800">
                {Math.round(displayProgress)}%
              </span>
            </div>
          </div>

          {/* Stage message only (no stage numbers) */}
          <div className="text-center">
            <p
              className="text-green-600 text-sm font-medium"
              style={{
                animation: 'subtle-fade 2s ease-in-out infinite'
              }}
            >
              {currentStage.message}
            </p>
          </div>

          {/* CSS Animation */}
          <style>{`
            @keyframes subtle-fade {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.6; }
            }
          `}</style>

          {onCancel && (
            <Button
              onClick={onCancel}
              variant="outline"
              className="bg-black hover:bg-gray-800 text-white px-8 mt-4"
            >
              Cancel
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
